import React, { useCallback } from 'react';
import { catchError, ignoreElements, mergeMap, subscribeOn } from 'rxjs';
import { asyncScheduler, concat, defer, EMPTY, Observable, of } from 'rxjs';
import Bugsnag from '@bugsnag/js';

import { useObservableValue } from '../useObservableValue';

export type PhotoSwipeImage = {
    src: string;
    w: number;
    h: number;
    msrc?: string;
    title?: string;
};

export type PhotoSwipeOpen = {
    images: PhotoSwipeImage[];
    index: number;
};

export enum PhotoSwipeEventName {
    Idle = 'idle',
    Loading = 'loading',
    Ready = 'ready',
}

export type PhotoSwipeEvent =
    | {
          type: PhotoSwipeEventName.Idle;
      }
    | {
          type: PhotoSwipeEventName.Loading;
          index: number;
      }
    | {
          type: PhotoSwipeEventName.Ready;
          html: string;
      };

/**
 * usePhotoswipe (PhotoSwipe v5)
 *
 * Returns [msg, dispatch]. When msg.type === Ready, render a placeholder div with ref so layout is stable.
 * Call dispatch({ images, index }) to open the lightbox.
 */
export function usePhotoswipe(ref: React.RefObject<HTMLElement>): [PhotoSwipeEvent, (input: PhotoSwipeOpen) => void] {
    const cb = useCallback(($values: Observable<PhotoSwipeOpen>) => {
        return $values.pipe(
            mergeMap((input) => {
                return concat(of({ type: PhotoSwipeEventName.Loading }), loadPhotoswipeV5(input));
            }),
        );
    }, []);
    return useObservableValue<PhotoSwipeOpen, any>(cb, { type: PhotoSwipeEventName.Idle }, 'usePhotoswipe');
}

function toV5DataSource(
    images: PhotoSwipeImage[],
): { src: string; width: number; height: number; msrc?: string; alt?: string }[] {
    return images.map((img) => ({
        src: img.src,
        width: img.w,
        height: img.h,
        ...(img.msrc && { msrc: img.msrc }),
        ...(img.title && { alt: img.title }),
    }));
}

function loadPhotoswipeV5(input: PhotoSwipeOpen): Observable<PhotoSwipeEvent> {
    return defer(() =>
        Promise.all([
            import(/* webpackChunkName:"__lazy__photoswipe" */ 'photoswipe'),
            import(/* webpackChunkName:"__lazy__photoswipe-style" */ 'photoswipe/style.css'),
            // Pure-CSS loading indicator, overriding photoswipe's inline SVG one.
            import(/* webpackChunkName:"__lazy__photoswipe-style" */ './photoswipePreloader.scss'),
        ]),
    ).pipe(
        mergeMap(([PhotoSwipeModule]) => {
            const PhotoSwipe = PhotoSwipeModule.default;
            const dataSource = toV5DataSource(input.images);
            return concat(
                of({
                    type: PhotoSwipeEventName.Ready,
                    html: '<div class="pswp" tabIndex="-1" role="dialog" aria-modal="true"></div>',
                } as PhotoSwipeEvent),
                defer(() => {
                    return new Observable<never>((obs) => {
                        const options = {
                            dataSource,
                            index: input.index,
                            showHideAnimationType: 'zoom' as const,
                        };
                        const pswp = new PhotoSwipe(options);
                        document.body.classList.add('modal-open');
                        pswp.on('close', () => {
                            setTimeout(() => document.body.classList.remove('modal-open'), 100);
                        });
                        pswp.init();
                        return () => {
                            try {
                                pswp.close();
                            } catch (_) {}
                        };
                    });
                }).pipe(subscribeOn(asyncScheduler), ignoreElements()),
            );
        }),
        catchError((e) => {
            Bugsnag.notify('photoswipe failed to load', (event) => {
                event.addMetadata('errorObject', e);
            });
            return EMPTY;
        }),
    );
}
